import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import TemplateList from './TemplateList';
import ActivityTable from './ActivityTable';
import MetricSection from './MetricSection';
import StatBox from './StatBox';
import { SHARED_TYPOGRAPHY } from '../../../theme';

/**
 * PerformanceSummaryAccordion Component
 *
 * Displays performance summary in a collapsible accordion with:
 * - Top Performers (best/worst templates)
 * - Recent Activity (table of recent executions)
 * - Performance Summary (key metrics and standout templates)
 * - Activity Summary (activity metrics and recent info)
 */
const PerformanceSummaryAccordion = ({ topPerformers, adapters }) => {
  // Prepare metrics data for MetricSection components (rating-based)
  const bestTemplates = topPerformers?.best_templates || [];
  const hasData =
    topPerformers &&
    (bestTemplates.length > 0 ||
      (topPerformers.recent_activity &&
        topPerformers.recent_activity.length > 0));
  const avgRating =
    bestTemplates.length > 0
      ? bestTemplates.reduce((sum, t) => sum + (t.avg_rating || 0), 0) /
        bestTemplates.length
      : 0;
  const totalFeedback = bestTemplates.reduce(
    (sum, t) => sum + (t.total_feedback || 0),
    0
  );

  const performanceMetrics = [
    {
      value: `${avgRating.toFixed(2)}`,
      label: 'Avg Rating',
      color: 'primary',
    },
    {
      value:
        bestTemplates.length > 0
          ? `${(
              bestTemplates.reduce((sum, t) => sum + (t.success_rate || 0), 0) /
              bestTemplates.length
            ).toFixed(1)}%`
          : '0%',
      label: 'Success Rate',
      color: 'success',
    },
    {
      value:
        bestTemplates.length > 0
          ? `${(
              bestTemplates.reduce(
                (sum, t) => sum + (t.performance_score || 0),
                0
              ) / bestTemplates.length
            ).toFixed(1)}`
          : '0',
      label: 'Performance Score',
      color: 'info',
    },
    {
      value: totalFeedback,
      label: 'Total Feedback',
      color: 'warning',
    },
  ];

  const activityMetrics =
    topPerformers?.recent_activity && topPerformers.recent_activity.length > 0
      ? [
          {
            value: topPerformers.recent_activity.length,
            label: 'Total Executions',
            color: 'primary',
          },
          {
            value: `${(
              (topPerformers.recent_activity.filter(a => a.status === 'success')
                .length /
                topPerformers.recent_activity.length) *
              100
            ).toFixed(1)}%`,
            label: 'Success Rate',
            color: 'success',
          },
          {
            value: new Set(
              topPerformers.recent_activity.map(a => a.template_id)
            ).size,
            label: 'Active Templates',
            color: 'info',
          },
          {
            value: (
              topPerformers.recent_activity.length /
              new Set(topPerformers.recent_activity.map(a => a.template_id))
                .size
            ).toFixed(1),
            label: 'Avg per Template',
            color: 'warning',
          },
        ]
      : [];

  return (
    <Box sx={{ mt: 4 }}>
      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="performance-summary-content"
          id="performance-summary-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main' }} />
            <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
              Performance Summary
            </Typography>
            <Typography
              variant="h6"
              sx={{ ...SHARED_TYPOGRAPHY.bodyMediumSecondary }}
            >
              ({topPerformers?.period || 'N/A'})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Top Performers */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={SHARED_TYPOGRAPHY.h5Bold}
                  >
                    Top Performers
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {hasData ? (
                      <TemplateList
                        templates={bestTemplates}
                        title="Best Templates (Rating-Based)"
                        titleColor="success.main"
                        chipColor="success"
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 4 }}
                      >
                        No performance data available
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={SHARED_TYPOGRAPHY.h5Bold}
                  >
                    Recent Activity
                  </Typography>
                  {hasData && topPerformers?.recent_activity ? (
                    <ActivityTable activities={topPerformers.recent_activity} />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      No recent activity data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Summary - Full Width */}
            <Grid item xs={12}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={SHARED_TYPOGRAPHY.h5Bold}
                  >
                    Performance Summary
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {/* Performance Distribution - Horizontal Layout */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{
                            ...SHARED_TYPOGRAPHY.bodyBoldSecondary,
                            mb: 1,
                            textAlign: 'left',
                          }}
                        >
                          Performance Distribution
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <StatBox
                            value={bestTemplates.length || 0}
                            label="Top Performers"
                            color="success"
                            sx={{ flex: '1 1 200px', minWidth: 200 }}
                          />
                          <StatBox
                            value={(
                              bestTemplates.reduce(
                                (sum, t) => sum + (t.avg_rating || 0),
                                0
                              ) / (bestTemplates.length || 1) || 0
                            ).toFixed(2)}
                            label="Avg Rating"
                            color="primary"
                            sx={{ flex: '1 1 200px', minWidth: 200 }}
                          />
                          <StatBox
                            value={bestTemplates[0]?.template_name || 'N/A'}
                            label="🏆 Best Performer"
                            color="success"
                            valueVariant="body2"
                            sx={{
                              flex: '1 1 200px',
                              minWidth: 200,
                              wordBreak: 'break-word',
                            }}
                          />
                          <StatBox
                            value={
                              bestTemplates[0]?.avg_rating
                                ? `${bestTemplates[0].avg_rating.toFixed(2)}/5`
                                : 'N/A'
                            }
                            label="⭐ Top Rating"
                            color="warning"
                            valueVariant="body2"
                            sx={{
                              flex: '1 1 200px',
                              minWidth: 200,
                              wordBreak: 'break-word',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Key Metrics - Responsive Grid: 4 columns on large, 2 on medium, 1 on small */}
                      {hasData ? (
                        <MetricSection
                          title="Key Metrics"
                          metrics={performanceMetrics}
                          gridColumns={{
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(4, 1fr)',
                          }}
                          gap={2}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          No performance metrics available
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Summary - Hidden per user request */}
            {false && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                  >
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={SHARED_TYPOGRAPHY.h5Bold}
                    >
                      Activity Summary ({topPerformers?.period || 'N/A'})
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      {topPerformers?.recent_activity ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ mb: 1 }}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              sx={{
                                ...SHARED_TYPOGRAPHY.bodyBoldSecondary,
                                mb: 0.5,
                                textAlign: 'left',
                              }}
                            >
                              Recent Activity Overview
                            </Typography>
                            <Box display="flex" gap={1.5}>
                              <StatBox
                                value={
                                  topPerformers.recent_activity.filter(
                                    a => a.status === 'success'
                                  ).length
                                }
                                label="Successful"
                                color="success"
                                flex
                              />
                              <StatBox
                                value={
                                  topPerformers.recent_activity.filter(
                                    a => a.status === 'failed'
                                  ).length
                                }
                                label="Failed"
                                color="error"
                                flex
                              />
                            </Box>
                          </Box>

                          <MetricSection
                            title="Activity Metrics"
                            metrics={activityMetrics}
                          />

                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                              sx={{ mb: 0.5, textAlign: 'left' }}
                              fontWeight="bold"
                            >
                              Recent Activity
                            </Typography>
                            <Box display="flex" gap={1}>
                              <StatBox
                                value={
                                  topPerformers.recent_activity[0]
                                    ? new Date(
                                        topPerformers.recent_activity[0]
                                          .last_execution
                                      ).toLocaleDateString()
                                    : 'N/A'
                                }
                                label="📅 Last Execution"
                                color="grey"
                                valueVariant="body2"
                                flex
                              />
                              <StatBox
                                value={
                                  topPerformers.recent_activity[0]
                                    ? (() => {
                                        const now = new Date();
                                        const lastExec = new Date(
                                          topPerformers.recent_activity[0]
                                            .last_execution
                                        );
                                        const diffHours = Math.floor(
                                          (now - lastExec) / (1000 * 60 * 60)
                                        );
                                        return diffHours < 24
                                          ? `${diffHours}h ago`
                                          : `${Math.floor(diffHours / 24)}d ago`;
                                      })()
                                    : 'N/A'
                                }
                                label="⏰ Time Since Last"
                                color="grey"
                                valueVariant="body2"
                                flex
                              />
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            No activity data available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PerformanceSummaryAccordion;
